import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faTruck,
  faUser,
  faMapMarkerAlt,
  faFileInvoice,
  faInfoCircle,
  faCalendarDay,
  faWeightHanging,
  faTag,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

const emDash = '—';

const InfoRow = ({ icon, label, value, highlight = false }) => {
  const displayValue = (value !== null && value !== undefined && value !== '') ? value : emDash;

  if (value === null || value === undefined || value === '') return null;

  return (
    <div
      className="flex items-start justify-between rounded-xl border px-4 py-3 h-full"
      style={{
        borderColor: UI_COLORS.BORDERS.LIGHT,
        backgroundColor: highlight
          ? colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1)
          : UI_COLORS.BACKGROUNDS.CARD || '#fff',
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
          style={{
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION?.HOVER || '#f0f0f0', 0.3),
            color: UI_COLORS.TEXT.PRIMARY,
          }}
        >
          <FontAwesomeIcon icon={icon} className="text-base" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide truncate" style={{ color: UI_COLORS.TEXT.MUTED }}>
            {label}
          </p>
          <p className="text-base font-medium truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {displayValue}
          </p>
        </div>
      </div>
    </div>
  );
};

const formatDate = (val) => {
  if (!val) return emDash;
  try {
    return new Date(val).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return emDash;
  }
};

const SectionHeader = ({ title, icon }) => (
  <div className="col-span-full mt-4 mb-2 flex items-center gap-2">
    <FontAwesomeIcon icon={icon} style={{ color: UI_COLORS.BRAND.PRIMARY }} className="text-xs" />
    <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
      {title}
    </h3>
  </div>
);

const VehicleTrackingModal = ({ isOpen, onClose, data, loading = false }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      <div 
        className="w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl relative" 
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD || '#fff' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-5" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {t('vehicleTracking.modal.title')}
            </p>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {data?.vehicle_number || emDash}
            </h2>
            <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {t('vehicleTracking.modal.orderPrefix')}{data?.order_number || emDash} / {t('vehicleTracking.modal.subOrderPrefix')}{data?.sub_order_number || emDash}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-6 px-6 py-6">
          {!data ? (
            <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <FontAwesomeIcon icon={faTruck} className="text-4xl opacity-60" style={{ color: UI_COLORS.TEXT.MUTED }} />
              <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('common.emptyStates.noData')}</h3>
              <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('vehicleTracking.emptyStates.noTracking')}</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              <SectionHeader title={t('vehicleTracking.sections.vehiclePersonnel')} icon={faTruck} />
              <InfoRow icon={faTruck} label={t('vehicleTracking.labels.vehicleNumber')} value={data.vehicle_number} highlight />
              <InfoRow icon={faUser} label={t('vehicleTracking.labels.driverContact')} value={data.driver_contact} />
              <InfoRow icon={faUser} label={t('vehicleTracking.labels.supervisor')} value={data.supervisor_name} />

              <SectionHeader title={t('vehicleTracking.sections.loadingWeights')} icon={faWeightHanging} />
              <InfoRow icon={faWeightHanging} label={t('vehicleTracking.labels.emptyTruckWeight')} value={data.empty_truck_weight ? `${data.empty_truck_weight} ${t('common.analyticsColumns.kg')}` : null} />
              <InfoRow icon={faWeightHanging} label={t('vehicleTracking.labels.finalTruckWeight')} value={data.final_truck_weight ? `${data.final_truck_weight} ${t('common.analyticsColumns.kg')}` : null} />
              <InfoRow icon={faInfoCircle} label={t('vehicleTracking.labels.vehicleRemark')} value={data.vehicle_remark} />

              <SectionHeader title={t('vehicleTracking.sections.orderContext')} icon={faInfoCircle} />
              <InfoRow icon={faTag} label={t('vehicleTracking.labels.orderNumber')} value={data.order_number} />
              <InfoRow icon={faTag} label={t('vehicleTracking.labels.subOrderNumber')} value={data.sub_order_number} />
              <InfoRow icon={faCalendarDay} label={t('vehicleTracking.labels.orderDate')} value={formatDate(data.order_date)} />
              <InfoRow icon={faInfoCircle} label={t('vehicleTracking.labels.orderStatus')} value={data.order_status} />
              <InfoRow icon={faInfoCircle} label={t('vehicleTracking.labels.subOrderStatus')} value={data.sub_order_status} />
              <InfoRow icon={faTag} label={t('vehicleTracking.labels.totalAmount')} value={data.sub_order_total_amount} />

              <SectionHeader title={t('vehicleTracking.sections.customerDelivery')} icon={faMapMarkerAlt} />
              <InfoRow icon={faUser} label={t('vehicleTracking.labels.customerName')} value={data.customer_name} />
              <InfoRow icon={faUser} label={t('vehicleTracking.labels.tallyName')} value={data.tally_name} />
              <InfoRow icon={faMapMarkerAlt} label={t('vehicleTracking.labels.deliveryLocation')} value={data.location_name} />

              <SectionHeader title={t('vehicleTracking.sections.materialWeights')} icon={faWeightHanging} />
              <InfoRow icon={faWeightHanging} label={t('vehicleTracking.labels.totalOrderWeight')} value={data.total_order_weight ? `${data.total_order_weight} ${t('common.analyticsColumns.kg')}` : null} />
              <InfoRow icon={faWeightHanging} label={t('vehicleTracking.labels.subOrderWeight')} value={data.sub_order_weight ? `${data.sub_order_weight} ${t('common.analyticsColumns.kg')}` : null} />
              <InfoRow icon={faInfoCircle} label={t('vehicleTracking.labels.unitType')} value={data.unit_type} />

              <SectionHeader title={t('vehicleTracking.sections.associatedDocuments')} icon={faFileInvoice} />
              <InfoRow icon={faFileInvoice} label={t('vehicleTracking.labels.challanNumber')} value={data.challan_number} />
              <InfoRow icon={faCalendarDay} label={t('vehicleTracking.labels.challanDate')} value={formatDate(data.challan_date)} />
              <InfoRow icon={faFileInvoice} label={t('vehicleTracking.labels.invoiceNumber')} value={data.invoice_number} />
              <InfoRow icon={faCalendarDay} label={t('vehicleTracking.labels.invoiceDate')} value={formatDate(data.invoice_date)} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t px-6 py-4" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <button
            onClick={onClose}
            className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: 'transparent',
              color: UI_COLORS.TEXT.MUTED,
              border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
            }}
          >
            {t('common.actions.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehicleTrackingModal;
